import React, { useEffect, useRef, useState } from 'react';

/**
 * Public customer-facing landing page. Entry point for the QR scan flow.
 *
 * URL: /t/<HOTELID>-<OUTLETID>-<TABLENO>-<SALT>
 *
 * P1c (this file):
 *   - Fetch /api/v1/self-order/t/:qrCode → outlet branding + table + menu
 *   - Render branded header (logo, outlet name, table label, welcome text)
 *   - Mobile input → Send OTP
 *   - OTP input → Verify → store session_token
 *   - Read-only menu preview
 *
 * P2 will add cart, place order, offer handling.
 *
 * This component renders OUTSIDE the staff App shell (see index.js). No
 * access to the staff theme; fully self-contained CSS-in-JS.
 */

const SESSION_KEY = 'self_order_session';

export default function SelfOrderLanding({ qrCode }) {
  const [loading, setLoading] = useState(true);
  const [landing, setLanding] = useState(null);
  const [error, setError] = useState('');

  // OTP flow state
  const [step, setStep] = useState('mobile'); // 'mobile' | 'otp' | 'verified'
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');
  const [session, setSession] = useState(null); // { session_token, session_id, ... }

  // Menu category filter
  const [dietFilter, setDietFilter] = useState('ALL'); // ALL | VEG | NONVEG

  // Age gate (bar outlets only)
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  // Cart + orders (P2)
  const [cart, setCart] = useState({});            // { [menu_item_id]: qty }
  const [cartNotes, setCartNotes] = useState({});  // { [menu_item_id]: "extra spicy" }
  const [noteEditId, setNoteEditId] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [placeStatus, setPlaceStatus] = useState(''); // '' | 'success' | 'error:...'
  const [myOrders, setMyOrders] = useState([]);
  const [orderTotals, setOrderTotals] = useState({ order_count: 0, item_count: 0, grand_total: 0 });
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [billRequested, setBillRequested] = useState(false);

  // Offer engine (P-D)
  const [offers, setOffers] = useState([]);                       // [{offer_id, status, saving, suggestion_text, …}]
  const [menuHints, setMenuHints] = useState({});                  // { [menu_item_id]: { offer_id, title, icon, short_text, rule_type } }
  const [dismissedOfferIds, setDismissedOfferIds] = useState(() => new Set());
  const [checkoutModal, setCheckoutModal] = useState({ open: false, offers: [] });
  const [acceptingOfferId, setAcceptingOfferId] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  // Staff-pushed offer (P-E) — shown as a distinctive toast banner at the top.
  const [pushedOffer, setPushedOffer] = useState(null);           // { event_id, offer_id, title, … }
  const [seenPushedEventIds, setSeenPushedEventIds] = useState(() => new Set());

  const otpRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/v1/self-order/t/${encodeURIComponent(qrCode)}`);
        const data = await res.json();
        if (!res.ok) { setError(data.error || 'QR code not recognised'); return; }
        setLanding(data);
        // Resume session if already verified
        const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
        if (stored?.qr_code === qrCode && stored?.session_token) {
          setSession(stored);
          setStep('verified');
        }
      } catch {
        setError('Unable to reach the server. Please try again.');
      } finally { setLoading(false); }
    })();
  }, [qrCode]);

  const sendOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) { setOtpMessage('Enter a valid 10-digit mobile starting with 6-9'); return; }
    setSending(true);
    setOtpMessage('');
    try {
      const res = await fetch('/api/v1/self-order/otp/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qrCode, mobile }),
      });
      const data = await res.json();
      if (!res.ok) { setOtpMessage(data.error || 'Failed to send OTP'); return; }
      setStep('otp');
      setOtpMessage(`OTP sent to ${mobile}. Enter it below.`);
      setTimeout(() => otpRef.current?.focus(), 100);
    } catch {
      setOtpMessage('Network error');
    } finally { setSending(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) { setOtpMessage('Enter the 6-digit OTP'); return; }
    setVerifying(true); setOtpMessage('');
    try {
      const res = await fetch('/api/v1/self-order/otp/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qrCode, mobile, otp }),
      });
      const data = await res.json();
      if (!res.ok) { setOtpMessage(data.error || 'OTP verification failed'); return; }
      const sessionPayload = { ...data, mobile, qr_code: qrCode };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionPayload));
      setSession(sessionPayload);
      setStep('verified');
      setOtpMessage('');
    } catch {
      setOtpMessage('Network error');
    } finally { setVerifying(false); }
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    setStep('mobile');
    setMobile(''); setOtp(''); setOtpMessage('');
    setCart({});
    setCartNotes({});
    setNoteEditId(null);
    setMyOrders([]);
    setBillRequested(false);
  };

  // ── Cart + order placement ───────────────────────────────────────────────
  const cartItemCount = Object.values(cart).reduce((s, q) => s + q, 0);
  const cartTotal = (landing?.menu || []).reduce((sum, m) => sum + (Number(m.price || 0) * (cart[m.id] || 0)), 0);

  const inc = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart(c => {
    const next = { ...c };
    if ((next[id] || 0) <= 1) {
      delete next[id];
      // Clear the note too if item is fully removed
      setCartNotes(n => { const m = { ...n }; delete m[id]; return m; });
      if (noteEditId === id) setNoteEditId(null);
    } else { next[id] = next[id] - 1; }
    return next;
  });

  const loadMyOrders = async () => {
    if (!session?.session_token) return;
    setOrdersLoading(true);
    try {
      const r = await fetch('/api/v1/self-order/orders', {
        headers: { Authorization: 'Bearer ' + session.session_token },
      });
      const data = await r.json();
      if (r.ok) {
        // New shape: { orders, totals } — fallback to array for backward compat
        if (Array.isArray(data)) { setMyOrders(data); }
        else {
          setMyOrders(Array.isArray(data?.orders) ? data.orders : []);
          setOrderTotals(data?.totals || { order_count: 0, item_count: 0, grand_total: 0 });
        }
      }
    } catch { /* silent */ }
    finally { setOrdersLoading(false); }
  };

  const loadPushedOffers = async () => {
    if (!session?.session_token) return;
    try {
      const r = await fetch('/api/v1/self-order/offers/pushed', {
        headers: { Authorization: 'Bearer ' + session.session_token },
      });
      if (!r.ok) return;
      const data = await r.json().catch(() => ({}));
      const pushed = Array.isArray(data?.pushed) ? data.pushed : [];
      const fresh = pushed.find(p => !seenPushedEventIds.has(p.event_id) && !dismissedOfferIds.has(p.offer_id));
      if (fresh) {
        setPushedOffer(fresh);
        setSeenPushedEventIds(s => { const n = new Set(s); n.add(fresh.event_id); return n; });
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (step !== 'verified') return;
    loadMyOrders();
    loadPushedOffers();
    // Poll every 20 s so the panel reflects KOT status + BILLED transitions
    // without the customer having to tap Refresh; also pick up staff-pushed offers.
    const t = setInterval(() => { loadMyOrders(); loadPushedOffers(); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, session?.session_token]);

  // ── Offers: debounced eligibility fetch when cart or orders change ──────
  const fetchOffers = async () => {
    if (!session?.session_token) return;
    try {
      const cartArr = Object.entries(cart).map(([menu_item_id, qty]) => ({ menu_item_id, qty: Number(qty || 0) }));
      const url = '/api/v1/self-order/offers/eligibility?cart=' + encodeURIComponent(JSON.stringify(cartArr));
      const r = await fetch(url, { headers: { Authorization: 'Bearer ' + session.session_token } });
      if (!r.ok) { if (r.status !== 429) setOffers([]); return; }
      const data = await r.json();
      setOffers(Array.isArray(data?.offers) ? data.offers : []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (step !== 'verified' || !session?.session_token) return;
    const t = setTimeout(fetchOffers, 600);  // debounce rapid cart taps
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, cart, myOrders.length, session?.session_token]);

  // ── Menu hints (pre-cart teaser badges) — KOT + BAR (backend dispatches) ──
  useEffect(() => {
    if (step !== 'verified' || !session?.session_token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/v1/self-order/offers/menu-hints', {
          headers: { Authorization: 'Bearer ' + session.session_token },
        });
        if (!r.ok) return;
        const data = await r.json().catch(() => ({}));
        if (!cancelled) setMenuHints(data?.hints && typeof data.hints === 'object' ? data.hints : {});
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [step, session?.session_token]);

  const acceptOffer = async (offer, triggerSource) => {
    if (!session?.session_token) return;
    setAcceptingOfferId(offer.offer_id);
    try {
      // NEARLY with a missing item — just add it to cart, let the customer tap Place Order when ready.
      if (offer.status === 'NEARLY' && offer.missing_item?.menu_item_id) {
        const id = String(offer.missing_item.menu_item_id);
        const n = Math.max(1, Math.ceil(Number(offer.missing_item.qty || 1)));
        setCart(c => ({ ...c, [id]: (c[id] || 0) + n }));
        setToastMessage(`Added ${offer.missing_item.name} · ${offer.title} will unlock`);
        setTimeout(() => setToastMessage(''), 3500);
        return;
      }
      // QUALIFIED — record the acceptance on the backend (server re-verifies).
      const cartArr = Object.entries(cart).map(([menu_item_id, qty]) => ({ menu_item_id, qty }));
      const r = await fetch(`/api/v1/self-order/offers/${offer.offer_id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.session_token },
        body: JSON.stringify({ cart: cartArr, trigger_source: triggerSource || 'CART_BANNER' }),
      });
      const data = await r.json();
      if (!r.ok) { setToastMessage(data.error || 'Could not apply offer'); setTimeout(() => setToastMessage(''), 3500); return; }
      // Auto-append BOGO / FREE_ITEM reward so the customer sees it in the cart.
      const rewardId = data?.offer?.reward_type === 'FREE_ITEM' || data?.offer?.rule_type === 'BOGO'
        ? offer.reward_item_id || data?.offer?.reward_item_id
        : null;
      if (rewardId) {
        setCart(c => ({ ...c, [String(rewardId)]: (c[String(rewardId)] || 0) + 1 }));
      }
      setToastMessage(`✓ Offer applied: ${offer.title}`);
      setTimeout(() => setToastMessage(''), 3500);
      fetchOffers();
    } catch {
      setToastMessage('Network error applying offer');
      setTimeout(() => setToastMessage(''), 3500);
    } finally {
      setAcceptingOfferId(null);
    }
  };

  const declineOffer = async (offer, triggerSource) => {
    // Key by (id, status) for cart-banner dismisses so a NEARLY → QUALIFIED
    // transition still surfaces. Pushed-offer dismisses (no status) fall back
    // to the raw offer_id.
    const dismissKey = offer?.status ? `${offer.offer_id}:${offer.status}` : offer.offer_id;
    setDismissedOfferIds(s => {
      const next = new Set(s);
      next.add(dismissKey);
      return next;
    });
    if (!session?.session_token) return;
    try {
      await fetch(`/api/v1/self-order/offers/${offer.offer_id}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.session_token },
        body: JSON.stringify({ trigger_source: triggerSource || 'CART_BANNER' }),
      });
    } catch { /* silent */ }
  };

  const placeOrder = async () => {
    if (!cartItemCount) return;
    setPlacing(true); setPlaceStatus('');
    try {
      const items = Object.entries(cart).map(([menu_item_id, qty]) => ({
        menu_item_id,
        qty,
        notes: (cartNotes[menu_item_id] || '').trim() || undefined,
      }));
      const r = await fetch('/api/v1/self-order/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.session_token },
        body: JSON.stringify({ items, guest_count: 1 }),
      });
      const data = await r.json();
      if (!r.ok) { setPlaceStatus('error:' + (data.error || 'Could not place order')); return; }
      setCart({});
      setCartNotes({});
      setNoteEditId(null);
      setPlaceStatus('success');
      await loadMyOrders();
      setTimeout(() => setPlaceStatus(''), 4000);
    } catch {
      setPlaceStatus('error:Network error');
    } finally { setPlacing(false); }
  };

  const sendBillRequest = async () => {
    try {
      const r = await fetch('/api/v1/self-order/bill/request', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + session.session_token },
      });
      if (r.ok) setBillRequested(true);
    } catch { /* silent */ }
  };

  // Before asking staff for the bill, surface any eligible offers (SRS §9.2).
  // If none, skip straight to the bill request.
  const requestBill = async () => {
    const checkoutOffers = offers.filter(o => o.show_on_checkout);
    if (!checkoutOffers.length) {
      if (!window.confirm('Request your bill now? Staff will bring it to your table or settle at the counter.')) return;
      await sendBillRequest();
      return;
    }
    setCheckoutModal({ open: true, offers: checkoutOffers });
  };

  const closeCheckoutModalAndSendBill = async () => {
    setCheckoutModal({ open: false, offers: [] });
    await sendBillRequest();
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return <Shell><div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#64748b' }}>Loading…</div></Shell>;
  }
  if (error) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <div style={{ fontSize: '3rem' }}>🚫</div>
          <h2 style={{ color: '#991b1b' }}>QR not recognised</h2>
          <p style={{ color: '#64748b' }}>{error}</p>
          <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Please ask the staff for the latest sticker.</p>
        </div>
      </Shell>
    );
  }

  const { hotel, outlet, table, menu } = landing;
  const filteredMenu = (menu || []).filter(m => {
    if (dietFilter === 'VEG') return m.is_veg === true;
    if (dietFilter === 'NONVEG') return m.is_veg === false;
    return true;
  });

  const needsAgeCheck = outlet?.requires_age_check && !ageConfirmed && step !== 'verified';

  return (
    <Shell>
      <Header hotel={hotel} outlet={outlet} table={table} />

      {needsAgeCheck && (
        <div style={card}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Age confirmation</h3>
          <p style={{ margin: '0 0 0.75rem', color: '#64748b', fontSize: '0.9rem' }}>
            This is a bar outlet. Please confirm you are of legal drinking age (21+ in Tamil Nadu).
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
            <input type="checkbox" onChange={(e) => setAgeConfirmed(e.target.checked)} />
            I confirm I am 21 years or older.
          </label>
        </div>
      )}

      {step === 'mobile' && !needsAgeCheck && (
        <div style={card}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Enter your mobile to start</h3>
          <p style={{ margin: '0 0 0.9rem', color: '#64748b', fontSize: '0.9rem' }}>
            We'll send a 6-digit OTP. No account or password needed.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <span style={{ padding: '0.75rem 0.8rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px 0 0 8px', fontWeight: 600, color: '#334155' }}>+91</span>
            <input
              inputMode="numeric" pattern="[0-9]*" maxLength={10}
              placeholder="10-digit mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              style={{ ...input, borderRadius: '0 8px 8px 0', flex: 1 }}
            />
          </div>
          <button onClick={sendOtp} disabled={sending || mobile.length !== 10}
            style={{ ...btnPrimary, marginTop: '0.75rem', opacity: sending || mobile.length !== 10 ? 0.6 : 1 }}>
            {sending ? 'Sending…' : 'Send OTP →'}
          </button>
          {otpMessage && <div style={{ marginTop: '0.6rem', color: '#b91c1c', fontSize: '0.88rem' }}>{otpMessage}</div>}
        </div>
      )}

      {step === 'otp' && (
        <div style={card}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Enter OTP</h3>
          <p style={{ margin: '0 0 0.9rem', color: '#64748b', fontSize: '0.88rem' }}>
            Sent to <b>+91 {mobile}</b>.{' '}
            <button onClick={() => { setStep('mobile'); setOtp(''); setOtpMessage(''); }}
              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '0.88rem' }}>
              Change number
            </button>
          </p>
          <input
            ref={otpRef}
            inputMode="numeric" pattern="[0-9]*" maxLength={6}
            placeholder="6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ ...input, fontSize: '1.4rem', letterSpacing: '0.4rem', textAlign: 'center', fontFamily: 'monospace' }}
          />
          <button onClick={verifyOtp} disabled={verifying || otp.length !== 6}
            style={{ ...btnPrimary, marginTop: '0.75rem', opacity: verifying || otp.length !== 6 ? 0.6 : 1 }}>
            {verifying ? 'Verifying…' : 'Verify & continue →'}
          </button>
          <button onClick={sendOtp} disabled={sending}
            style={{ ...btnGhost, marginTop: '0.5rem' }}>
            {sending ? 'Resending…' : 'Resend OTP'}
          </button>
          {otpMessage && <div style={{ marginTop: '0.6rem', color: otpMessage.toLowerCase().includes('sent') ? '#065f46' : '#b91c1c', fontSize: '0.88rem' }}>{otpMessage}</div>}
        </div>
      )}

      {step === 'verified' && (
        <div style={{ ...card, background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)', color: '#fff', borderColor: '#065f46' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>✓ Verified</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>+91 {session?.mobile || mobile}</div>
            </div>
            <button onClick={logout} style={{ ...btnGhost, color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>End</button>
          </div>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.88rem', opacity: 0.95 }}>
            Tap <b>Add</b> on any item below, then <b>Place order</b>. Your kitchen gets it instantly.
          </p>
        </div>
      )}

      {placeStatus === 'success' && (
        <div style={{ ...card, background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46', textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>🎉</div>
          <div style={{ fontWeight: 700 }}>Order placed!</div>
          <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>The kitchen is preparing your items.</div>
        </div>
      )}
      {placeStatus.startsWith('error:') && (
        <div style={{ ...card, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
          {placeStatus.slice(6)}
        </div>
      )}

      {step === 'verified' && myOrders.length > 0 && myOrders.every(o => o.status === 'BILLED') && (
        <div style={{ ...card, background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)', color: '#fff', borderColor: '#065f46', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: 4 }}>✨</div>
          <h3 style={{ margin: '0 0 0.3rem', fontSize: '1.25rem' }}>Thank you!</h3>
          <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>
            Your bill of <b>₹{Number(orderTotals.grand_total || 0).toFixed(0)}</b> has been settled.
          </div>
          <div style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: '0.5rem' }}>
            We hope you enjoyed your meal at <b>{hotel?.name}</b>. See you again soon!
          </div>
          <button onClick={logout}
            style={{ marginTop: '0.9rem', padding: '0.55rem 1.3rem', background: '#fff', color: '#065f46', border: 'none', borderRadius: 999, fontWeight: 700, cursor: 'pointer' }}>
            End session
          </button>
        </div>
      )}

      {step === 'verified' && myOrders.length > 0 && !myOrders.every(o => o.status === 'BILLED') && (
        <div style={card}>
          {/* Running summary bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 0.9rem', marginBottom: '0.75rem',
            background: 'linear-gradient(135deg, #eff6ff 0%, #ddd6fe 100%)',
            borderRadius: 12, border: '1px solid #c7d2fe',
          }}>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Your Orders</div>
              <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: 2 }}>
                <b style={{ color: '#1e3a8a' }}>{orderTotals.order_count}</b> order{orderTotals.order_count === 1 ? '' : 's'} · <b style={{ color: '#1e3a8a' }}>{orderTotals.item_count}</b> item{orderTotals.item_count === 1 ? '' : 's'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Running Total</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#1e3a8a', letterSpacing: '-0.01em' }}>₹{Number(orderTotals.grand_total || 0).toFixed(0)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
            <button onClick={loadMyOrders} disabled={ordersLoading}
              style={{ background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.3rem 0.7rem', fontSize: '0.78rem', color: '#475569', cursor: 'pointer' }}>
              {ordersLoading ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>

          {myOrders.map(o => (
            <div key={o.id} style={{ padding: '0.75rem 0.9rem', marginBottom: '0.6rem', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(15,23,42,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>#{o.order_no}</span>
                  <span style={{
                    padding: '3px 10px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em',
                    background: o.status === 'BILLED' ? '#dbeafe' : o.status === 'READY' ? '#dcfce7' : '#fef3c7',
                    color:      o.status === 'BILLED' ? '#1e40af' : o.status === 'READY' ? '#166534' : '#92400e',
                  }}>{o.status}</span>
                </div>
                <span style={{ fontWeight: 900, color: '#1e40af', fontSize: '1.05rem' }}>₹{Number(o.order_total || 0).toFixed(0)}</span>
              </div>
              {o.items?.length > 0 && (
                <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '0.4rem' }}>
                  {o.items.map(it => (
                    <div key={it.id} style={{ padding: '0.3rem 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.86rem' }}>
                        <span style={{ color: '#0f172a' }}>
                          <b style={{ color: '#1e3a8a' }}>{Number(it.qty)}×</b> {it.display_name}
                        </span>
                        <span style={{ color: '#334155', fontWeight: 600 }}>₹{Number(it.line_total || 0).toFixed(0)}</span>
                      </div>
                      {it.remarks && (
                        <div style={{
                          marginTop: 3, padding: '3px 8px',
                          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
                          fontSize: '0.74rem', color: '#78350f', fontStyle: 'italic',
                        }}>📝 {it.remarks}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!billRequested ? (
            <button onClick={requestBill}
              style={{
                width: '100%', padding: '0.75rem 1rem', marginTop: '0.5rem',
                background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                color: '#fff', border: 'none', borderRadius: 12,
                fontSize: '0.95rem', fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 6px 14px rgba(245,158,11,0.3)',
              }}>
              🧾 Request Bill · ₹{Number(orderTotals.grand_total || 0).toFixed(0)}
            </button>
          ) : (
            <div style={{ marginTop: '0.5rem', padding: '0.65rem 0.8rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, color: '#92400e', fontSize: '0.88rem', textAlign: 'center', fontWeight: 600 }}>
              ⏳ Bill requested — staff has been notified. Please wait or proceed to the counter.
            </div>
          )}
        </div>
      )}

      {/* Menu preview (read-only in P1; cart + order comes in P2) */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1rem 0.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Menu</h3>
          <span style={{ color: '#64748b', fontSize: '0.82rem' }}>{filteredMenu.length} item{filteredMenu.length === 1 ? '' : 's'}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', padding: '0.5rem 1rem 0.8rem' }}>
          {[
            { key: 'ALL', label: 'All' },
            { key: 'VEG', label: '🟢 Veg' },
            { key: 'NONVEG', label: '🔴 Non-veg' },
          ].map(f => (
            <button key={f.key} onClick={() => setDietFilter(f.key)}
              style={{
                padding: '0.38rem 0.9rem', borderRadius: 999, border: '1px solid',
                borderColor: dietFilter === f.key ? '#2563eb' : '#e2e8f0',
                background: dietFilter === f.key ? '#2563eb' : '#fff',
                color: dietFilter === f.key ? '#fff' : '#334155',
                fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              }}>
              {f.label}
            </button>
          ))}
        </div>
        {filteredMenu.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: '#94a3b8', fontSize: '0.9rem' }}>
            <div style={{ fontSize: '2.2rem', marginBottom: '0.3rem' }}>🍽️</div>
            No menu items match this filter.
          </div>
        ) : (
          <div>
            {filteredMenu.map(item => {
              const inCart = (cart[item.id] || 0) > 0;
              const noteOpen = noteEditId === item.id;
              const noteValue = cartNotes[item.id] || '';
              return (
              <div key={item.id} style={{
                display: 'flex', flexDirection: 'column', gap: '0.5rem',
                padding: '0.9rem 1rem',
                borderTop: '1px solid #f1f5f9',
              }}>
              <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: 3 }}>
                    <VegBadge isVeg={item.is_veg} />
                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem', lineHeight: 1.2 }}>{item.name}</span>
                  </div>
                  {item.sku && <div style={{ color: '#94a3b8', fontSize: '0.68rem', fontFamily: 'monospace', letterSpacing: '0.04em' }}>{item.sku}</div>}
                  {(() => {
                    const hint = menuHints[String(item.id)];
                    if (!hint) return null;
                    return (
                      <div title={hint.title} style={{
                        marginTop: '0.35rem',
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        color: '#92400e',
                        border: '1px solid #fcd34d',
                        borderRadius: 999,
                        padding: '0.18rem 0.55rem',
                        fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2,
                        maxWidth: '100%',
                      }}>
                        <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>{hint.icon}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hint.short_text}</span>
                      </div>
                    );
                  })()}
                  <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e40af' }}>₹{Number(item.price || 0).toFixed(0)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                  {item.image_url ? (
                    <img src={item.image_url} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 12, background: '#f1f5f9' }} />
                  ) : (
                    <div style={{ width: 84, height: 84, borderRadius: 12, background: 'linear-gradient(135deg, #eef2ff 0%, #ddd6fe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>
                      {item.is_veg === false ? '🍖' : '🍽️'}
                    </div>
                  )}
                  {step === 'verified' && (
                    inCart ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.15rem', background: '#fff', border: '2px solid #1e40af', borderRadius: 999, padding: '2px' }}>
                        <button onClick={() => dec(item.id)}
                          style={{ width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'transparent', color: '#1e40af', fontSize: '1.1rem', fontWeight: 900, cursor: 'pointer' }}>−</button>
                        <span style={{ width: 24, textAlign: 'center', fontWeight: 800, color: '#1e40af' }}>{cart[item.id]}</span>
                        <button onClick={() => inc(item.id)}
                          style={{ width: 26, height: 26, borderRadius: '50%', border: 'none', background: '#1e40af', color: '#fff', fontSize: '1.1rem', fontWeight: 900, cursor: 'pointer' }}>+</button>
                      </div>
                    ) : (
                      <button onClick={() => inc(item.id)}
                        style={{ padding: '0.35rem 0.9rem', border: '2px solid #1e40af', background: '#fff', color: '#1e40af', borderRadius: 999, fontSize: '0.78rem', fontWeight: 800, cursor: 'pointer' }}>
                        ADD
                      </button>
                    )
                  )}
                </div>
              </div>
              {/* Per-item special-instructions — shown only when qty > 0 */}
              {step === 'verified' && inCart && (
                <div>
                  {noteOpen ? (
                    <textarea
                      autoFocus
                      placeholder="Any special instructions for this item? (e.g. extra spicy, no onion, less oil)"
                      value={noteValue}
                      onChange={(e) => setCartNotes(n => ({ ...n, [item.id]: e.target.value.slice(0, 300) }))}
                      onBlur={() => setNoteEditId(null)}
                      rows={2}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        padding: '0.5rem 0.7rem', border: '1px solid #cbd5e1',
                        borderRadius: 8, fontSize: '0.85rem', resize: 'vertical',
                        background: '#fffbeb', outline: 'none',
                      }}
                    />
                  ) : noteValue ? (
                    <button onClick={() => setNoteEditId(item.id)}
                      style={{ background: '#fffbeb', border: '1px dashed #fde68a', color: '#92400e', width: '100%', padding: '0.4rem 0.6rem', borderRadius: 8, fontSize: '0.78rem', textAlign: 'left', cursor: 'pointer' }}>
                      📝 <i>{noteValue}</i> · <span style={{ textDecoration: 'underline' }}>edit</span>
                    </button>
                  ) : (
                    <button onClick={() => setNoteEditId(item.id)}
                      style={{ background: 'transparent', border: '1px dashed #cbd5e1', color: '#64748b', width: '100%', padding: '0.4rem 0.6rem', borderRadius: 8, fontSize: '0.78rem', textAlign: 'left', cursor: 'pointer' }}>
                      + Add special instruction
                    </button>
                  )}
                </div>
              )}
            </div>
            );})}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.72rem', padding: '1rem', marginBottom: cartItemCount > 0 ? '6rem' : '1.5rem' }}>
        Powered by EASEPMS · QR <span style={{ fontFamily: 'monospace' }}>{qrCode}</span>
      </div>

      {/* ─── Offer banner (P-D) — shown above the sticky cart bar ─── */}
      {step === 'verified' && (() => {
        const topOffer = offers.find(o => o.show_on_cart && !dismissedOfferIds.has(`${o.offer_id}:${o.status}`));
        if (!topOffer) return null;
        const isQualified = topOffer.status === 'QUALIFIED';
        const bg = isQualified
          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
          : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
        const accepting = acceptingOfferId === topOffer.offer_id;
        return (
          <div style={{
            position: 'fixed', bottom: cartItemCount > 0 ? 86 : 16,
            left: 0, right: 0, zIndex: 55,
            padding: '0 0.8rem',
          }}>
            <div style={{ maxWidth: 520, margin: '0 auto' }}>
              <div style={{
                background: bg, color: '#fff',
                borderRadius: 14, padding: '0.85rem 0.95rem',
                boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                display: 'flex', alignItems: 'center', gap: '0.7rem',
              }}>
                <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>{isQualified ? '🎁' : '⭐'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.88rem', letterSpacing: '0.01em' }}>
                    {topOffer.title}
                  </div>
                  <div style={{ fontSize: '0.78rem', opacity: 0.92, marginTop: 2 }}>
                    {topOffer.suggestion_text}
                  </div>
                </div>
                <button
                  onClick={() => acceptOffer(topOffer, 'CART_BANNER')}
                  disabled={accepting}
                  style={{
                    background: '#fff', color: isQualified ? '#059669' : '#b45309',
                    border: 'none', borderRadius: 10, padding: '0.45rem 0.8rem',
                    fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}>
                  {accepting ? '…' : (isQualified ? 'Apply' : 'Add')}
                </button>
                <button
                  onClick={() => declineOffer(topOffer, 'CART_BANNER')}
                  aria-label="Dismiss"
                  style={{
                    background: 'rgba(255,255,255,0.18)', color: '#fff',
                    border: 'none', borderRadius: 8, padding: '0.35rem 0.45rem',
                    fontSize: '0.9rem', cursor: 'pointer', lineHeight: 1,
                  }}>✕</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sticky cart bar — shown only when items in cart and session verified */}
      {step === 'verified' && cartItemCount > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(180deg, rgba(248,250,252,0) 0%, #f8fafc 15%)',
          padding: '1rem 0.8rem calc(env(safe-area-inset-bottom, 0.6rem) + 0.8rem)',
          zIndex: 50,
        }}>
          <div style={{ maxWidth: 520, margin: '0 auto' }}>
            <button onClick={placeOrder} disabled={placing}
              style={{
                width: '100%', padding: '0.9rem 1rem',
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                color: '#fff', border: 'none', borderRadius: 14,
                fontSize: '1rem', fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 10px 24px rgba(16,185,129,0.4)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
                opacity: placing ? 0.7 : 1,
              }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 999, padding: '3px 10px', fontSize: '0.82rem', fontWeight: 800 }}>
                  {cartItemCount}
                </span>
                <span>{placing ? 'Placing…' : 'Place Order'}</span>
              </span>
              <span style={{ fontSize: '1.05rem', fontWeight: 900 }}>₹{Number(cartTotal).toFixed(0)}</span>
            </button>
          </div>
        </div>
      )}

      {/* ─── Staff-pushed offer toast (P-E) ─── */}
      {pushedOffer && step === 'verified' && (
        <div style={{
          position: 'fixed', top: 14, left: 0, right: 0, zIndex: 90,
          display: 'flex', justifyContent: 'center', padding: '0 0.8rem',
        }}>
          <div style={{
            width: '100%', maxWidth: 520,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            color: '#fff', borderRadius: 14, padding: '0.85rem 1rem',
            boxShadow: '0 14px 36px rgba(99,102,241,0.55)',
            display: 'flex', alignItems: 'center', gap: '0.75rem',
          }}>
            <span style={{ fontSize: '1.7rem', lineHeight: 1 }}>🎁</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.72rem', opacity: 0.88, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Your server sent an offer
              </div>
              <div style={{ fontWeight: 800, fontSize: '0.92rem', marginTop: 2 }}>{pushedOffer.title}</div>
              {pushedOffer.description && (
                <div style={{ fontSize: '0.78rem', opacity: 0.92, marginTop: 2 }}>{pushedOffer.description}</div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button
                onClick={async () => {
                  // Reuse acceptOffer — but the staff-push row is ALREADY "OFFERED",
                  // so we just POST /accept with trigger_source=STAFF_PUSH.
                  const synthOffer = {
                    offer_id: pushedOffer.offer_id,
                    status: 'QUALIFIED',      // treat as qualified since staff explicitly pushed it
                    title: pushedOffer.title,
                    reward_type: pushedOffer.reward_type,
                    reward_item_id: pushedOffer.reward_item_id,
                    rule_type: pushedOffer.rule_type,
                    suggestion_text: pushedOffer.title,
                  };
                  await acceptOffer(synthOffer, 'STAFF_PUSH');
                  setPushedOffer(null);
                }}
                style={{
                  background: '#fff', color: '#4f46e5', border: 'none',
                  borderRadius: 8, padding: '0.4rem 0.7rem',
                  fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer',
                }}>Accept</button>
              <button
                onClick={() => {
                  declineOffer({ offer_id: pushedOffer.offer_id }, 'STAFF_PUSH');
                  setPushedOffer(null);
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '0.35rem 0.7rem',
                  fontSize: '0.75rem', cursor: 'pointer',
                }}>Skip</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Toast (offer accept/decline feedback) ─── */}
      {toastMessage && (
        <div style={{
          position: 'fixed', top: 14, left: 0, right: 0, zIndex: 80,
          display: 'flex', justifyContent: 'center', padding: '0 0.8rem',
          pointerEvents: 'none',
        }}>
          <div style={{
            background: '#0f172a', color: '#fff',
            padding: '0.6rem 0.9rem', borderRadius: 12,
            fontSize: '0.85rem', fontWeight: 600,
            boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
            maxWidth: 520,
          }}>{toastMessage}</div>
        </div>
      )}

      {/* ─── Request Bill — offer modal (SRS §9.2) ─── */}
      {checkoutModal.open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(15,23,42,0.75)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div style={{
            width: '100%', maxWidth: 520,
            background: '#fff', borderRadius: '24px 24px 0 0',
            padding: '1.3rem 1.1rem calc(env(safe-area-inset-bottom, 0.6rem) + 1.1rem)',
            boxShadow: '0 -20px 50px rgba(0,0,0,0.35)',
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{ fontSize: '2.2rem', lineHeight: 1 }}>
                {checkoutModal.offers.some(o => o.status === 'QUALIFIED') ? '🎁' : '⭐'}
              </div>
              <h2 style={{ margin: '0.4rem 0 0.2rem', fontSize: '1.2rem', fontWeight: 900, color: '#0f172a' }}>
                {checkoutModal.offers.some(o => o.status === 'QUALIFIED')
                  ? "You've earned a discount"
                  : "Wait — you're close to a discount"}
              </h2>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                Apply it before we close your bill.
              </p>
            </div>

            {/* Offer cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', marginBottom: '0.9rem', maxHeight: '50vh', overflowY: 'auto' }}>
              {checkoutModal.offers.map(o => {
                const accepting = acceptingOfferId === o.offer_id;
                return (
                  <div key={o.offer_id} style={{
                    border: '1px solid #e2e8f0', borderRadius: 14,
                    padding: '0.85rem', background: '#f8fafc',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: 4 }}>
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 800,
                        padding: '2px 8px', borderRadius: 999,
                        background: o.status === 'QUALIFIED' ? '#d1fae5' : '#fef3c7',
                        color: o.status === 'QUALIFIED' ? '#065f46' : '#92400e',
                      }}>{o.status}</span>
                      {o.saving > 0 && (
                        <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#059669' }}>
                          Save ₹{Number(o.saving).toFixed(0)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>{o.title}</div>
                    <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: 3, marginBottom: 10 }}>
                      {o.suggestion_text}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={async () => {
                          await acceptOffer(o, 'CHECKOUT_MODAL');
                          // NEARLY → close modal so they can adjust cart; QUALIFIED → keep modal open to keep going.
                          if (o.status === 'NEARLY') setCheckoutModal({ open: false, offers: [] });
                        }}
                        disabled={accepting}
                        style={{
                          flex: 1, padding: '0.6rem', borderRadius: 10,
                          background: o.status === 'QUALIFIED'
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          color: '#fff', border: 'none', fontWeight: 800, fontSize: '0.88rem',
                          cursor: 'pointer', opacity: accepting ? 0.7 : 1,
                        }}>
                        {accepting ? '…' : (o.status === 'QUALIFIED' ? 'Apply offer' : `Add ${o.missing_item?.name || 'item'}`)}
                      </button>
                      <button
                        onClick={() => declineOffer(o, 'CHECKOUT_MODAL')}
                        style={{
                          flex: '0 0 auto', padding: '0.6rem 0.9rem', borderRadius: 10,
                          background: '#fff', color: '#64748b', border: '1px solid #cbd5e1',
                          fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                        }}>Skip</button>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={closeCheckoutModalAndSendBill}
              style={{
                width: '100%', padding: '0.8rem', borderRadius: 12,
                background: '#0f172a', color: '#fff', border: 'none',
                fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer',
              }}>
              Skip all — Request bill
            </button>
            <button
              onClick={() => setCheckoutModal({ open: false, offers: [] })}
              style={{
                width: '100%', marginTop: 6, padding: '0.5rem',
                background: 'none', color: '#64748b', border: 'none',
                fontSize: '0.82rem', cursor: 'pointer',
              }}>Cancel</button>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ─── Shell + header (reusable) ───────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '0 0.8rem' }}>
        {children}
      </div>
    </div>
  );
}

function Header({ hotel, outlet, table }) {
  const hotelInitial = String(hotel?.name || '?').charAt(0).toUpperCase();
  const outletInitial = String(outlet?.name || '?').charAt(0).toUpperCase();
  return (
    <div style={{
      background: 'linear-gradient(140deg, #1e1b4b 0%, #3730a3 35%, #2563eb 100%)',
      color: '#fff',
      margin: '0 -0.8rem 1rem',
      padding: '1.4rem 1.2rem 2.3rem',
      borderRadius: '0 0 28px 28px',
      boxShadow: '0 10px 28px rgba(30,27,75,0.35)',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative circles */}
      <div aria-hidden style={{ position: 'absolute', top: -60, right: -60, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)' }} />
      <div aria-hidden style={{ position: 'absolute', bottom: -80, left: -80, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 70%)' }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Hotel row — compact pill with logo + name */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(6px)',
          padding: '0.3rem 0.7rem 0.3rem 0.35rem', borderRadius: 999,
          fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em',
          marginBottom: '1rem', maxWidth: '90%',
        }}>
          {hotel?.logo_url ? (
            <img src={hotel.logo_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%', background: '#fff', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#fff', color: '#1e1b4b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800 }}>{hotelInitial}</div>
          )}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
            {hotel?.name || 'Hotel'}
            {hotel?.city ? <span style={{ opacity: 0.75 }}> · {hotel.city}</span> : null}
          </span>
        </div>

        {/* Outlet hero */}
        {outlet?.logo_url ? (
          <img src={outlet.logo_url} alt={outlet.name}
            style={{ height: 72, width: 72, objectFit: 'cover', marginBottom: '0.6rem', background: '#fff', borderRadius: 18, padding: 6, boxShadow: '0 6px 14px rgba(0,0,0,0.25)' }} />
        ) : (
          <div style={{
            width: 72, height: 72, margin: '0 auto 0.6rem',
            background: 'linear-gradient(135deg, #fff 0%, #e0e7ff 100%)',
            color: '#1e1b4b',
            borderRadius: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem', fontWeight: 900,
            boxShadow: '0 6px 14px rgba(0,0,0,0.25)',
          }}>{outletInitial}</div>
        )}
        <div style={{ fontSize: '1.7rem', fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{outlet?.name || 'Outlet'}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.7rem', padding: '0.4rem 1rem', background: 'rgba(255,255,255,0.18)', borderRadius: 999, fontSize: '0.92rem', fontWeight: 700 }}>
          <span>🪑</span>
          <span>Table <b>{table?.table_no || '—'}</b></span>
          {table?.zone && <span style={{ opacity: 0.85 }}>· {table.zone}</span>}
        </div>
        {outlet?.welcome_text && (
          <div style={{ fontSize: '0.92rem', opacity: 0.95, marginTop: '0.9rem', fontStyle: 'italic', lineHeight: 1.4 }}>
            “{outlet.welcome_text}”
          </div>
        )}
      </div>
    </div>
  );
}

function VegBadge({ isVeg }) {
  if (isVeg === true) {
    return <span title="Veg" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, border: '1.5px solid #22c55e', borderRadius: 3 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e' }} />
    </span>;
  }
  if (isVeg === false) {
    return <span title="Non-veg" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, border: '1.5px solid #dc2626', borderRadius: 3 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc2626' }} />
    </span>;
  }
  return null;
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const card = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: '1rem 1.1rem',
  marginBottom: '0.9rem',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
};
const input = {
  padding: '0.75rem 0.85rem',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: '1rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  background: '#fff',
};
const btnPrimary = {
  width: '100%',
  padding: '0.8rem 1rem',
  border: 'none',
  borderRadius: 10,
  background: '#1e40af',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(30,64,175,0.3)',
};
const btnGhost = {
  width: '100%',
  padding: '0.65rem 1rem',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  background: 'transparent',
  color: '#475569',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
};
